/**
 * `useSlowRequest()` — React binding for the slow-request signal.
 *
 * Returns true while at least one API call has been outstanding longer than
 * {@link SLOW_REQUEST_THRESHOLD_MS}. Apps pair it with `<WakingUpNotice>` from
 * `@mangrove/ui` to explain a Cloud Run cold start instead of showing a frozen
 * button for half a minute. See `../slow-request.ts` for why.
 */
import { useSyncExternalStore } from 'react';
import {
  isRequestSlow,
  slowRequestReason,
  subscribeSlowRequest,
  type SlowRequestReason,
} from '../slow-request.js';

/** Server snapshot: never "slow" during SSR — there is nothing in flight. */
const serverSnapshot = (): boolean => false;

export function useSlowRequest(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => subscribeSlowRequest(() => onStoreChange()),
    isRequestSlow,
    serverSnapshot,
  );
}

const noReason = (): SlowRequestReason | null => null;

/**
 * Why the slow flag is up — `'checking'`, `'busy'` (the server answered a
 * question that needs no database, so it is up) or `'unreachable'` — and null
 * while it is down. The notice words itself from this instead of guessing (#621).
 */
export function useSlowRequestReason(): SlowRequestReason | null {
  return useSyncExternalStore(
    (onStoreChange) => subscribeSlowRequest(() => onStoreChange()),
    slowRequestReason,
    noReason,
  );
}
